import { useState, useRef } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AbcIcon from '@mui/icons-material/Abc';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { menuItemSx, deleteItemSx, iconSx, deleteIconSx, separatorSx } from './noteOptionsSx';
import { useContentPanelContext } from '../../../contexts/ContentPanelContext';
import { tooltipSx } from '../../../components/shared/jernyTooltipStyling';
import noteOptionsPanelEngine from './Core/NoteOptionsPanelEngine';
import bodyEditorEngine from '../../../BodyEditorWidget/Core/BodyEditorEngine';

const disabledItemSx = { ...menuItemSx, opacity: 0.35, cursor: 'default', pointerEvents: 'none' };
const disabledDeleteSx = { ...deleteItemSx, opacity: 0.35, cursor: 'default', pointerEvents: 'none' };

const subMenuSx = {
    position: 'fixed',
    zIndex: 10000,
    background: '#0d1f33',
    border: '0.5px solid #294766',
    borderRadius: '4px',
    fontFamily: "'DM Sans', sans-serif",
    color: '#b0c8e0',
    minWidth: 180,
    boxShadow: '0 8px 28px rgba(0,0,0,0.6)',
};

function ActionRow({ icon, label, onClick, sx = menuItemSx, endIcon }) {
    return (
        <Box sx={{ ...sx, justifyContent: 'flex-start' }} onClick={(e) => onClick?.(e)}>
            {icon}
            <span style={{ flex: 1 }}>{label}</span>
            {endIcon}
        </Box>
    );
}

// FIXED — ShareSubMenu now receives entry so handleEmail can build the payload from live
// data instead of the engine's internal (possibly stale) copy.
function ShareSubMenu({ pos, onClose, entry, panelId }) {
    return (
        <Box sx={{ ...subMenuSx, top: pos.top, left: pos.left }} onMouseLeave={onClose}>
            <ActionRow
                icon={<FileDownloadOutlinedIcon sx={iconSx} />}
                label="Download"
                onClick={() => {
                    noteOptionsPanelEngine.handleDownload(panelId);
                    onClose();
                }}
            />
            <ActionRow
                icon={<EmailOutlinedIcon sx={iconSx} />}
                label="Send via email"
                onClick={() => {
                    noteOptionsPanelEngine.handleEmail(entry, panelId);
                    onClose();
                }}
            />
            <ActionRow
                icon={<ShareOutlinedIcon sx={iconSx} />}
                label="Share with Jerny user"
                onClick={() => {
                    noteOptionsPanelEngine.handleShare(panelId);
                    onClose();
                }}
            />
        </Box>
    );
}

/**
 * @param {object} props
 * @param {object} props.entry
 * @param {function} props.onClose
 * @param {string} [props.panelId='default']
 */
export default function NoteOptionsPanelActions({ entry, onClose, panelId = 'default' }) {
    const { isEditing } = useContentPanelContext();
    const [copiedBody, setCopiedBody] = useState(false);
    const [copiedRk, setCopiedRk] = useState(false);
    const [subMenuPos, setSubMenuPos] = useState(null);
    const shareRowRef = useRef(null);

    const showNewNote = noteOptionsPanelEngine.showNewNote();

    const handleCopyRk = () => {
        if (!entry?.rk) return;
        navigator.clipboard.writeText(entry.rk).then(() => {
            setCopiedRk(true);
            setTimeout(() => setCopiedRk(false), 1500);
        });
    };

    const handleCopyBody = () => {
        if (!entry?.body) return;
        navigator.clipboard.writeText(entry.body).then(() => {
            setCopiedBody(true);
            setTimeout(() => setCopiedBody(false), 1500);
        });
    };

    const handleShareHover = () => {
        if (!shareRowRef.current) return;
        const rect = shareRowRef.current.getBoundingClientRect();
        const subMenuWidth = 180;
        const spaceRight = window.innerWidth - rect.right;
        const left = spaceRight >= subMenuWidth ? rect.right : rect.left - subMenuWidth;
        setSubMenuPos({ top: rect.top, left });
    };

    const handleShareLeave = (e) => {
        if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
        setSubMenuPos(null);
    };

    const handleDelete = () => {
        noteOptionsPanelEngine.openDelete(panelId);
        onClose();
    };

    return (
        <>
            {showNewNote && (
                <ActionRow
                    icon={<NoteAddOutlinedIcon sx={iconSx} />}
                    label="New note from this"
                    onClick={(e) => {
                        // this opens CreateNoteDlg
                        noteOptionsPanelEngine.openNewNote(e, panelId);

                        // closes NoteOptionsPanel
                        onClose();
                    }}
                    sx={isEditing ? disabledItemSx : menuItemSx}
                />
            )}

            <ActionRow
                icon={<EditNoteIcon sx={iconSx} />}
                label="Edit body"
                onClick={() => {
                    // FIXED — was noteOptionsPanelEngine.getEntry(panelId), the engine's
                    // internal (possibly stale) copy. Uses the live entry prop instead.
                    bodyEditorEngine.open(entry);
                    onClose();
                }}
            />

            <ActionRow
                icon={<AbcIcon sx={iconSx} />}
                label="Edit body as text"
                onClick={() => {
                    // FIXED — passes the live entry prop, startEditing no longer reads p.entry
                    noteOptionsPanelEngine.startEditing(entry, panelId);
                    onClose();
                }}
            />

            <ActionRow
                icon={copiedBody ? <CheckIcon sx={iconSx} /> : <ContentCopyIcon sx={iconSx} />}
                label="Copy body"
                onClick={handleCopyBody}
            />

            <Box sx={separatorSx} />

            <Box ref={shareRowRef} onMouseEnter={handleShareHover} onMouseLeave={handleShareLeave}>
                <ActionRow
                    icon={<ShareOutlinedIcon sx={iconSx} />}
                    label="Share"
                    endIcon={<ChevronRightIcon sx={{ fontSize: 13, color: '#4a6a8a', ml: 'auto' }} />}
                />
                {subMenuPos && <ShareSubMenu pos={subMenuPos} onClose={() => setSubMenuPos(null)} entry={entry} panelId={panelId} />}
            </Box>

            <Box sx={separatorSx} />

            <ActionRow
                icon={<DeleteOutlineIcon sx={isEditing ? { ...deleteIconSx, opacity: 0.35 } : deleteIconSx} />}
                label="Delete entry"
                onClick={handleDelete}
                sx={isEditing ? disabledDeleteSx : deleteItemSx}
            />

            <Tooltip title={copiedRk ? 'Copied!' : 'Copy Id'} componentsProps={tooltipSx}>
                <Box
                    sx={{
                        px: 1.5,
                        pt: '6px',
                        pb: '4px',
                        borderTop: '0.5px solid #294766',
                        mt: '4px',
                        bgcolor: '#0b1e2e',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                    }}
                    onClick={handleCopyRk}
                >
                    {copiedRk ? (
                        <CheckIcon sx={{ fontSize: 10, color: '#4cceac' }} />
                    ) : (
                        <ContentCopyIcon sx={{ fontSize: 10, color: '#a3d1ff' }} />
                    )}
                    <Typography sx={{ fontSize: 10, color: '#a3d1ff', fontFamily: 'monospace' }}>{entry?.rk ?? '—'}</Typography>
                </Box>
            </Tooltip>
        </>
    );
}
